<!-- /**
 * 이 Vue 컴포넌트는 실시간 다국어 화상 회의 기능을 제공하는 UI 및 로직을 포함합니다.
 * Jitsi Meet와 FastAPI를 연동하여 화상 회의, 자막 표시, 음성 번역, 화면 공유, 
 * 사용자 제어(오디오/비디오 토글), 회의 링크 복사 및 RAG 기반 AI 어시스턴트 알림 기능을 제공합니다.
 * 또한 WebSocket을 이용한 번역 이벤트 수신과 REST API 기반 회의 종료 처리도 포함됩니다.
 * 주요 기능:
 * - Jitsi Meet를 통한 화상 회의
 * - 음성 번역 시작/종료 (REST API)
 * - 자막 실시간 표시 (WebSocket)
 * - RAG 기반 알림 (WebSocket)
 * - 오디오/비디오/화면 공유 제어
 * - 회의 종료 및 자원 정리
 */ -->


<template>
  <div class="meeting-container">
    <ReportGenerationModal v-if="isGeneratingReport" :progress="reportGenerationProgress" />
    <!-- ToastNotification 컴포넌트 제거 -->

    <!-- Jitsi Meet 컨테이너 -->
    <div id="jitsi-container" class="jitsi-container"></div>
    
    <!-- 신규: 발언 상태 표시줄 (Jitsi 영상 중앙) -->
    <div v-if="activeSpeaker && activeSpeaker.user_id !== userId" class="speaker-status-banner">
      <span>{{ activeSpeaker.user_name }} is talking...</span>
    </div>

    <!-- 자막 영역 추가 (Jitsi 컨테이너 바로 다음에 추가) -->
    <div class="subtitle-container" v-if="subtitle">
      <div class="subtitle-text">{{ subtitle }}</div>
    </div>

    <!-- 🚫 무음 경고 배너 (transition 추가) -->
    <transition name="silence-banner">
      <div v-if="silenceWarningShown" class="silence-warning-banner">
        <div class="warning-content">
          <i class="fas fa-microphone-slash"></i>
          <span>마이크에 음성이 감지되지 않아 자동으로 중지되었습니다.</span>
          <button @click="silenceWarningShown = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </transition>

    <!-- RAG 결과 알림 패널 (설정 섹션 위에 표시) -->
    <div class="rag-notification-content" v-if="isRagNotificationExpanded">
      <div class="notification-card">
        <div class="card-header">
          <h5>회의 어시스턴트</h5>
          <div class="header-controls">
            <button class="nav-button" @click="showPreviousNotification" :disabled="currentNotificationIndex >= ragNotifications.length - 1">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="notification-index">{{ currentNotificationIndex + 1 }}/{{ ragNotifications.length }}</span>
            <button class="nav-button" @click="showNextNotification" :disabled="currentNotificationIndex <= 0">
              <i class="fas fa-chevron-right"></i>
            </button>
            <button class="btn-close" @click="toggleRagPanel"></button>
          </div>
        </div>
        <div class="card-body">
          <h6 class="notification-title">{{ currentNotification.title || '회의 정보' }}</h6>
          <p class="notification-content" v-html="formatContent(currentNotification.content)"></p>
          <span class="notification-time">{{ formatNotificationTime(currentNotification.timestamp) }}</span>
        </div>
      </div>
    </div>

    <!-- 하단 컨트롤 패널 -->
    <div class="bottom-controls-container">
      <!-- 번역 설정 섹션 -->
      <div class="horizontal-controls-panel">
        <!-- 번역 옵션 선택 -->
        <div class="control-item option-selector">
          <div class="btn-group">
            <button 
              class="btn" 
              :class="translationOption === 'audio' ? 'btn-audio-active' : 'btn-audio-inactive'"
              @click="translationOption = 'audio'" 
              :disabled="isOptionsDisabled">
            <i class="fas fa-volume-up"></i>
            </button>
            <button 
              class="btn" 
              :class="translationOption === 'subtitle' ? 'btn-subtitle-active' : 'btn-subtitle-inactive'"
              @click="translationOption = 'subtitle'" 
              :disabled="isOptionsDisabled">
            <i i class="fas fa-closed-captioning"></i>
            </button>
          </div>
        </div>

        <!-- 번역 버튼 -->
        <div class="control-item translation-control">
          <button
            @click="toggleTranslation"
            :disabled="isTranslationDisabled || isRequestingToSpeak"
            :class="[
              'btn',
              isTranslating ? 'btn-stop' : 'btn-start',
              { 'is-disabled': isTranslationDisabled },
              { 'voice-active': isVoiceDetected }
            ]"
            :style="getButtonBorderStyle()">
            <i v-if="isRequestingToSpeak" class="fas fa-spinner fa-spin"></i>
            <i v-else :class="[isTranslating ? 'fas fa-stop-circle' : 'fas fa-play-circle']"></i>
            <span v-if="!isRequestingToSpeak">{{ isTranslating ? 'STOP' : 'TRANSLATION' }}</span>
          </button>
        </div>

        <!-- 화면 공유 버튼 -->
        <div class="control-item screen-share-control">
          <button 
            @click="toggleScreenShare" 
            :class="['btn', isScreenSharing ? 'btn-screen-share-on' : 'btn-screen-share-off']"
            @mouseenter="showTooltip('Screen Sharing')" @mouseleave="hideTooltip" aria-label="Screen Sharing">
            <i class="fas fa-desktop"></i>
            <div v-if="tooltip === 'Screen Sharing'" class="btn-tooltip">Screen Sharing</div>
          </button>
        </div>
        
        <!-- 오디오 제어 -->
        <div class="control-item audio-control">
          <button 
            @click="toggleAudio" 
            :class="['btn', isAudioEnabled ? 'btn-audio-on' : 'btn-audio-off']">
            <i :class="[isAudioEnabled ? 'fas fa-microphone' : 'fas fa-microphone-slash']"></i>
            <!-- <span>{{ isAudioEnabled ? '음소거' : '음소거 해제' }}</span> -->
          </button>
        </div>
        
        <!-- 비디오 제어 -->
        <div class="control-item video-control">
          <button 
            @click="toggleVideo" 
            :class="['btn', isVideoEnabled ? 'btn-video-on' : 'btn-video-off']">
            <i :class="[isVideoEnabled ? 'fas fa-video' : 'fas fa-video-slash']"></i>
            <!-- <span>{{ isVideoEnabled ? '화면 끄기' : '화면 켜기' }}</span> -->
          </button>
        </div>

        <!-- 회의 코드 복사 (기존 회의 링크 복사 부분 수정) -->
        <div class="control-item copy-code-control">
          <button @click="copyMeetingCode" :disabled="!joinCode" class="btn btn-copy-code"
          @mouseenter="showTooltip('Copy Code')" @mouseleave="hideTooltip" aria-label="Copy Code">
          <i v-if="!codeCopied" class="fas fa-key"></i>
          <i v-else class="fas fa-check"></i>
          </button>
          <div v-if="tooltip === 'Copy Code'" class="btn-tooltip">Copy Code</div>
        </div>

        <!-- 회의 종료 -->
        <div class="control-item end-meeting-control">
          <button class="btn btn-end-meeting" @click="leaveMeeting"
          :disabled="isLeavingMeeting"
          :class="{ 'leaving': isLeavingMeeting}" 
          @mouseenter="showTooltip('End Class')" @mouseleave="hideTooltip" aria-label="End Meeting">
            <i class="fas fa-phone-slash"></i>
            <div v-if="tooltip === 'End Class'" class="btn-tooltip">End Meeting</div>
          </button>
        </div>
        
        <!-- RAG 알림 버튼 (설정 섹션 내부 오른쪽에 배치) -->
        <div class="control-item rag-notification-control">
          <button 
            class="btn-rag-notification" 
            :class="{ 'blink': hasNewNotification }"
            @click="toggleRagPanel"
            @mouseenter="showTooltip('AI Assistant')" @mouseleave="hideTooltip" aria-label="AI Assistant">
            <i class="fas fa-robot"></i>
            <span class="notification-count" v-if="hasNewNotification">!</span>
          </button>
          <div v-if="tooltip === 'AI Assistant'" class="btn-tooltip">AI Assistant</div>
        </div>

        <!-- 스크립트 버튼 (직접 추가) -->
        <div class="control-item script-toggle-control">
          <button 
            class="btn btn-script-toggle" 
            :class="{ 'active': isScriptPanelOpen,'new-message': hasNewScriptMessage}"
            @click="toggleScriptPanel"
            @mouseenter="showTooltip('Conversation')" @mouseleave="hideTooltip" aria-label="Conversation">
            <i class="fas fa-comments"></i>
          </button>
          <div v-if="tooltip === 'Conversation'" class="btn-tooltip">Conversation</div>
        </div>
      </div>
    </div>

    <ScriptPanel 
        :script-history="scriptHistory"
        :is-open="isScriptPanelOpen"
        @toggle="toggleScriptPanel"
        @clear="clearScript"
        @close="closeScriptPanel"
        />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import fastApiWebSocket from '@/services/fast-api/fastApiWebSocket';
// 번역 API 가져오기
import {
  startTranslation,
  stopTranslation,
  getAvailableLanguages,
  cleanup as cleanupTranslation,
  setAudioLevelCallback,  // ✨ 추가
  cancelSilentRecording   // ✨ 추가
} from '@/services/fast-api/fastApiRESTAPI';
// ToastNotification import 제거
// API 호출을 위한 axios 추가
// apiConfig.js에서 필요한 설정값들을 가져옵니다.
// apiClientV1: FastAPI 서버와 통신 (번역 시작/종료, 회의 종료 등)
// jaasAppId, jitsiServerURL, jitsiScriptUrl: Jitsi Meet 연동에 필요한 설정값
import { apiClientV1, jaasAppId, jitsiServerURL, jitsiScriptUrl } from '@/api/apiConfig';
import { languageToEnglishName } from '@/constants/languages';
//import ISO6391 from 'iso-639-1';
//import api from '@/api/axios'; // Axios 인스턴스 가져오기

//스크립트
import ScriptPanel from '@/components/ScriptPanel.vue'
import ReportGenerationModal from '@/components/modals/ReportGenerationModal.vue'
// 스크립트 관련 상태
const scriptHistory = ref([])
const isScriptPanelOpen = ref(false)   // 기본적으로 닫혀있게
const hasNewScriptMessage = ref(false)  //새로운 누적 메세지 감지 시 깜빡임 

const route = useRoute();
const router = useRouter();

// --- 발언권 관리를 위한 신규 상태 변수 ---
const activeSpeaker = ref(null); // { user_id: string, user_name: string } | null
const isRequestingToSpeak = ref(false);

// 회의 정보
const meetingId = route.params.meetingId;
const joinCode = ref(''); // 회의 코드를 저장할 새로운 ref 추가
const userId = ref('');
const userName = ref('');

// 토큰 관리 상태 추가
const securityToken = ref(null);
const isTokenValid = ref(false);

// 회의 코드 복사 관련 상태
const codeCopied = ref(false);

// 오디오/비디오 상태
const isAudioEnabled = ref(route.query.audio === 'true');
const isVideoEnabled = ref(route.query.video === 'true');

// Jitsi Meet API 객체 참조
const jitsiApi = ref(null);

// RAG 알림 관련 상태
const ragNotifications = ref([]);
const hasNewNotification = ref(false);
const isRagNotificationExpanded = ref(false);
const currentNotificationIndex = ref(0);
const currentNotification = ref({});

// 번역 관련 상태
const isTranslating = ref(false);
const sourceLanguage = ref('');
const availableLanguages = ref([]);
const dropdownOpen = ref(false);

// 번역 옵션 (audio 또는 subtitle)
const translationOption = ref('subtitle'); // 기본값은 자막

// 🎤 음성 감지 관련 상태
const audioLevel = ref(0);  // 현재 음량 레벨 (0-100)
const isVoiceDetected = ref(false);  // 음성 감지 여부
const silenceWarningShown = ref(false);  // 무음 경고 표시 여부
const userInitiatedRecording = ref(false);  // ✨ 사용자가 직접 버튼을 눌러 녹음을 시작했는지 여부
let silenceTimer = null;  // 3초 무음 타이머
let voiceDetectionCount = 0;  // 연속 음성 감지 카운터

// 음성 감지 설정값
const VOICE_THRESHOLD = 20;  // 음성 감지 임계값 (0-100 범위) - 잡음 필터링 강화
const VOICE_DETECTION_FRAMES = 3;  // 연속 N프레임 이상 감지되어야 음성으로 인정
const SILENCE_TIMEOUT = 3000;  // 3초 (밀리초)

// 자막 관련 상태 추가
const subtitle = ref('');
const subtitleTimeout = ref(null);
const isScreenSharing = ref(false);

// 회의 종료 상태 추가
const isLeavingMeeting = ref(false); // 초기값 false
const isGeneratingReport = ref(false);
const reportGenerationProgress = ref(0);

//버튼 상태 변화
const tooltip = ref(null)
function showTooltip(name) { tooltip.value = name }
function hideTooltip() { tooltip.value = null }

// --- 신규: 버튼 비활성화 로직 ---
const isTranslationDisabled = computed(() => {
  // 다른 사람이 발언 중일 때 비활성화
  return activeSpeaker.value && activeSpeaker.value.user_id !== userId.value;
});

const isOptionsDisabled = computed(() => {
  // 번역 중이거나, 다른 사람이 발언 중일 때 비활성화
  return isTranslating.value || (activeSpeaker.value && activeSpeaker.value.user_id !== userId.value);
});

// 회의 무효화 확인 함수
function checkMeetingRevocation() {
  try {
    const revokedKey = `meeting_${meetingId}_revoked`;
    const isRevoked = sessionStorage.getItem(revokedKey);
    
    if (isRevoked) {
      console.error('회의가 무효화됨:', meetingId);
      alert('이 회의는 더 이상 유효하지 않습니다.');
      router.push('/dashboard');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('회의 무효화 확인 중 오류:', error);
    return false;
  }
}

// 새로운 토큰 교차 검증 함수 수정
function validateTokenCrossCheck() {
  try {
    console.log('토큰 검증 시작');
    
    // 1. URL에서 토큰 추출
    const urlKey = route.query.key;
    const urlToken = route.query.token;
    const urlCode = route.query.code;
    const urlUserName = route.query.userName;
    
    if (!urlKey || !urlToken || !urlCode) {
      console.error('URL 파라미터 누락');
      return false;
    }
    
    // 2. URL 토큰 디코딩
    let urlDecodedToken;
    try {
      urlDecodedToken = JSON.parse(atob(urlToken));
    } catch (error) {
      console.error('URL 토큰 디코딩 실패:', error);
      return false;
    }
    
    // 3. 기본 토큰 유효성 검사
    if (Date.now() > urlDecodedToken.expiresAt) {
      console.error('토큰 만료됨');
      return false;
    }
    
    if (urlDecodedToken.meetingId !== meetingId) {
      console.error('URL meetingId와 토큰 불일치');
      return false;
    }
    
    // 4. sessionStorage 확인
    const sessionTokenStr = sessionStorage.getItem('meetingSecurityToken');
    const sessionCode = sessionStorage.getItem('currentMeetingCode');
    const sessionMeetingId = sessionStorage.getItem('currentMeetingId');
    
    // 5. sessionStorage가 있는 경우 (새로고침)
    if (sessionTokenStr && sessionCode && sessionMeetingId) {
      console.log('📱 sessionStorage 토큰 발견 - 새로고침');
      
      let sessionToken;
      try {
        sessionToken = JSON.parse(sessionTokenStr);
      } catch (error) {
        console.error('sessionStorage 토큰 파싱 실패 - URL 토큰으로 재설정');
        return setupTokenFromUrl(urlDecodedToken, urlCode, urlUserName);
      }
      
      // URL 토큰과 sessionStorage 토큰 교차 검증
      if (urlDecodedToken.accessKey === sessionToken.accessKey &&
          urlDecodedToken.meetingId === sessionToken.meetingId &&
          urlDecodedToken.timestamp === sessionToken.timestamp &&
          urlDecodedToken.checksum === sessionToken.checksum) {
        
        console.log('🟢 기존 sessionStorage 토큰 검증 성공');
        return setupTokenAndUserInfo(sessionToken, urlCode, urlUserName);
      } else {
        console.warn('sessionStorage 토큰 불일치 - URL 토큰으로 재설정');
        return setupTokenFromUrl(urlDecodedToken, urlCode, urlUserName);
      }
    }
    
    // 6. sessionStorage가 없는 경우 - 새로운 접근
    console.log('🌐 sessionStorage 없음 - 새로운 접근');
    
    // 접근 소스 체크 (URL 복사 방지)
    const accessSource = detectAccessSource();
    
    if (accessSource === 'direct-url') {
      console.error('❌ 직접 URL 접근 감지 - 차단');
      return false;
    }
    
    // 허용된 접근
    return setupTokenFromUrl(urlDecodedToken, urlCode, urlUserName);
    
  } catch (error) {
    console.error('토큰 검증 중 오류:', error);
    return false;
  }
}

// 접근 소스 감지 (URL 복사 vs 정상 접근)
function detectAccessSource() {
  try {
    // 1. 같은 도메인에서 온 경우 (정상 접근)
    if (document.referrer && new URL(document.referrer).origin === window.location.origin) {
      console.log('🔗 내부 접근 감지');
      return 'internal-navigation';
    }
    
    // 2. 새로고침 체크
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation && navigation.type === 'reload') {
      console.log('🔄 새로고침 감지');
      return 'reload';
    }
    
    // 3. 뒤로가기/앞으로가기
    if (navigation && navigation.type === 'back_forward') {
      console.log('↩️ 뒤로가기/앞으로가기 감지');
      return 'back-forward';
    }
    
    // 4. 외부 접근 또는 직접 입력
    console.log('❌ 외부 접근 또는 직접 URL 입력 감지');
    return 'direct-url';
    
  } catch (error) {
    console.error('접근 소스 감지 오류:', error);
    return 'unknown';
  }
}

// URL 토큰으로 새로 설정
function setupTokenFromUrl(urlDecodedToken, urlCode, urlUserName) {
  try {
    console.log('🔧 URL 토큰으로 새로 설정');
    
    // sessionStorage에 저장
    sessionStorage.setItem('meetingSecurityToken', JSON.stringify(urlDecodedToken));
    sessionStorage.setItem('currentMeetingCode', atob(urlCode));
    sessionStorage.setItem('currentMeetingId', urlDecodedToken.meetingId);
    
    return setupTokenAndUserInfo(urlDecodedToken, urlCode, urlUserName);
    
  } catch (error) {
    console.error('URL 토큰 설정 오류:', error);
    return false;
  }
}

// 토큰 및 사용자 정보 설정
function setupTokenAndUserInfo(token, urlCode, urlUserName) {
  try {
    // 회의 정보 설정
    securityToken.value = token;
    joinCode.value = atob(urlCode);
    isTokenValid.value = true;
    
    // 사용자 이름 설정
    if (urlUserName) {
      try {
        userName.value = decodeURIComponent(atob(urlUserName));
      } catch (error) {
        console.error('URL 사용자 이름 디코딩 실패:', error);
        userName.value = '알수없음';
      }
    } else {
      userName.value = '알수없음';
    }
    
    console.log('🟢 토큰 및 사용자 정보 설정 완료');
    return true;
    
  } catch (error) {
    console.error('토큰 설정 중 오류:', error);
    return false;
  }
}




// 사용자 정보 설정 함수
function setupUserInfoFromSession() {
  try {
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      userId.value = userData.id ? userData.id.toString() : '';
      if (!userName.value) {
        userName.value = userData.name || '알수없음';
      }
    } else {
      userId.value = 'guest-' + crypto.randomUUID();
      if (!userName.value) {
        userName.value = '게스트';
      }
    }
  } catch (error) {
    console.error('사용자 정보 설정 중 오류:', error);
    userId.value = 'guest-' + crypto.randomUUID();
    userName.value = '게스트';
  }
}

// 세션 정리 함수
function cleanupTokens() {
  try {
    console.log('토큰 정리 시작');
    
    securityToken.value = null;
    isTokenValid.value = false;
    
    sessionStorage.removeItem('meetingSecurityToken');
    sessionStorage.removeItem('currentMeetingCode');
    sessionStorage.removeItem('currentMeetingId');
    
    console.log('토큰 정리 완료');
  } catch (error) {
    console.error('토큰 정리 중 오류:', error);
  }
}

// 페이지 이탈 핸들러 (로깅용으로만)
function handleBeforeUnload() {
  console.log('페이지 이탈 감지');
  // sessionStorage는 건드리지 않음 - 브라우저가 자동 관리
}

// Visibility API를 활용한 탭 상태 감지 강화
function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    console.log('탭 숨김 감지');
    // 탭이 숨겨질 때는 토큰을 정리하지 않음 (다시 활성화될 수 있음)
  } else if (document.visibilityState === 'visible') {
    console.log('탭 활성화 감지');
    // 필요시 토큰 유효성 재확인
  }
}

// 다중 이벤트 등록
function setupPageUnloadHandlers() {
  console.log('페이지 이탈 이벤트 핸들러 등록');
  
  // beforeunload는 로깅용으로만
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // visibilitychange는 유지 (향후 확장 가능)
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

// 회의 나가기 함수 수정 - 완전한 토큰 정리
async function leaveMeeting() {
  if (confirm('정말로 강의를 나가시겠습니까?')) {
    isLeavingMeeting.value = true;
    
    // 보고서 생성 모달 표시
    isGeneratingReport.value = true;
    reportGenerationProgress.value = 0;

    // 가상 프로그레스 바 시뮬레이션
    const reportGenerationTime = 10000; // 예: 10초
    let progressInterval = null;
    try {
      const startTime = Date.now();
      progressInterval = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const progress = (elapsedTime / reportGenerationTime) * 100;
        reportGenerationProgress.value = Math.min(99, Math.floor(progress));
      }, 150);

      // API 호출 로직
      await apiClientV1.post(`/api/v1/endMeeting?meetingId=${meetingId}`, {
        userId: userId.value
      });

      console.log('회의 종료 API 호출 성공');
      reportGenerationProgress.value = 100;

    } catch (error) {
      console.error('회의 종료 API 호출 실패:', error);
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      isGeneratingReport.value = false;
    }
    
    // 회의 무효화 마킹
    try {
      sessionStorage.setItem(`meeting_${meetingId}_revoked`, 'true');
    } catch (error) {
      console.error('회의 무효화 마킹 실패:', error);
    }
    
    // 자원 정리
    cleanupResources();
    
    // 토큰 정리
    cleanupTokens();

    // 창 닫기
    window.close();
    
    setTimeout(() => {
      router.push('/dashboard');
    }, 500);
  }
}

// // 기존 번역 관련 함수들 유지
// function toggleDropdown() {
//   if (!isTranslating.value) {
//     dropdownOpen.value = !dropdownOpen.value;
//   }
// }

// function selectLanguage(lang) {
//   sourceLanguage.value = lang;
//   dropdownOpen.value = false;
// }

function closeDropdownOnOutsideClick(event) {
  const dropdown = document.querySelector('.custom-dropdown');
  if (dropdown && !dropdown.contains(event.target)) {
    dropdownOpen.value = false;
  }
}

function initTranslationOptions() {
  availableLanguages.value = getAvailableLanguages();
  const storedUser = sessionStorage.getItem('user');
  if (storedUser) {
    // 현지화된 언어를 영어로 변경해주는 커스텀 매핑 함수. language.js에서 가지고옴
    const languageName = languageToEnglishName[JSON.parse(storedUser).language];
    console.log(languageName);
    if (languageName) {
      sourceLanguage.value = languageName;
    } else {
      sourceLanguage.value = 'Korean'; // 기본값 설정
      console.error(`알 수 없는 언어 코드여서 Korean으로 변경`);
    }
  }
}

// RAG 알림 관련 함수 개선
function toggleRagPanel() {
  console.log('RAG 패널 토글 요청, 현재 알림 개수:', ragNotifications.value.length);
  
  // 알림이 없으면 패널을 열지 않음
  if (ragNotifications.value.length === 0) {
    console.log('알림이 없어서 패널을 열지 않습니다.');
    return;
  }
  
  // 패널 상태 토글
  isRagNotificationExpanded.value = !isRagNotificationExpanded.value;
  console.log('RAG 패널 상태 변경:', isRagNotificationExpanded.value ? '열림' : '닫힘');
  
  // 패널을 열 때 처리
  if (isRagNotificationExpanded.value) {
    // 가장 최근 알림 표시
    currentNotificationIndex.value = 0;
    currentNotification.value = ragNotifications.value[0];
    console.log('현재 표시 알림:', currentNotification.value);
    
    // 알림 읽음 상태로 변경
    hasNewNotification.value = false;
    console.log('알림 읽음 처리: hasNewNotification =', hasNewNotification.value);
  }
}

function showNextNotification() {
  if (currentNotificationIndex.value > 0) {
    currentNotificationIndex.value--;
    currentNotification.value = ragNotifications.value[currentNotificationIndex.value];
  }
}

function showPreviousNotification() {
  if (currentNotificationIndex.value < ragNotifications.value.length - 1) {
    currentNotificationIndex.value++;
    currentNotification.value = ragNotifications.value[currentNotificationIndex.value];
  }
}

function formatNotificationTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

// 오디오 토글 함수
function toggleAudio() {
  jitsiApi.value.executeCommand('toggleAudio');
  isAudioEnabled.value = !isAudioEnabled.value;
}

// 비디오 토글 함수
function toggleVideo() {
  jitsiApi.value.executeCommand('toggleVideo');
  isVideoEnabled.value = !isVideoEnabled.value;
}

// RAG 결과 이벤트 핸들러 등록 수정
function initRagResultHandler() {
  console.log('RAG 결과 핸들러 초기화');
  
  fastApiWebSocket.addEventListener('rag_result', (ragResult) => {
    console.log('RAG 결과 이벤트 수신:', ragResult);
    
    // 문자열 정리 함수 - 특수문자 처리
    const cleanContent = (str) => {
      if (!str) return '';
      // 리터럴 \n 문자열을 실제 줄바꿈으로 변환 (JSON으로 받은 경우)
      return str.replace(/\\n/g, '\n');
    };
    
    // 알림 추가 (가장 최근 내용이 앞에 오도록)
    ragNotifications.value.unshift({
      title: ragResult.title || '회의 정보',
      content: cleanContent(ragResult.content || ragResult.message || JSON.stringify(ragResult)),
      timestamp: new Date().toISOString()
    });
    
    console.log('RAG 알림 추가됨, 현재 갯수:', ragNotifications.value.length);
    
    // 최대 10개까지만 유지
    if (ragNotifications.value.length > 10) {
      ragNotifications.value = ragNotifications.value.slice(0, 10);
    }
    
    // 새 알림이 들어왔을 때만 깜빡임 효과 활성화
    hasNewNotification.value = true;
    console.log('새 알림 상태 변경: hasNewNotification =', hasNewNotification.value);
  });
}

// ============================================================================
// 🎤 음성 모니터링 관련 함수들
// ============================================================================

/**
 * 🎤 음성 레벨 모니터링 시작
 */
function startVoiceMonitoring() {
  console.log('🎤 [음성 모니터링] 시작 (임계값: ' + VOICE_THRESHOLD + ', 연속 감지: ' + VOICE_DETECTION_FRAMES + '프레임, 사용자 시작: ' + userInitiatedRecording.value + ')');

  // 상태 초기화
  audioLevel.value = 0;
  isVoiceDetected.value = false;
  silenceWarningShown.value = false;
  voiceDetectionCount = 0;  // 카운터 초기화

  // ✨ 사용자가 직접 버튼을 눌렀을 때만 무음 타이머 시작
  // 서버에서 자동 발언권 부여 시에는 타이머를 시작하지 않음
  if (userInitiatedRecording.value) {
    console.log('🎤 [무음 타이머] 사용자 액션으로 인해 3초 타이머 시작');
    silenceTimer = setTimeout(() => {
      handleSilenceTimeout();
    }, SILENCE_TIMEOUT);
  } else {
    console.log('🎤 [무음 타이머] 서버 자동 부여이므로 타이머 비활성화');
    silenceTimer = null;
  }

  // 음성 레벨 콜백 등록
  setAudioLevelCallback((level) => {
    audioLevel.value = level;

    // 임계값 이상이면 카운터 증가
    if (level > VOICE_THRESHOLD) {
      voiceDetectionCount++;

      // 연속으로 N프레임 이상 감지되어야 실제 음성으로 인정
      if (voiceDetectionCount >= VOICE_DETECTION_FRAMES) {
        if (!isVoiceDetected.value) {
          console.log(`🎤 [음성 감지] 음량: ${level} (연속 ${voiceDetectionCount}프레임)`);
          isVoiceDetected.value = true;
        }

        // ✨ 사용자가 직접 시작한 경우에만 타이머 리셋
        if (userInitiatedRecording.value && silenceTimer) {
          clearTimeout(silenceTimer);
          silenceTimer = setTimeout(() => {
            handleSilenceTimeout();
          }, SILENCE_TIMEOUT);
        }
      }
    } else {
      // 임계값 이하면 카운터 리셋 (잡음 필터링)
      if (voiceDetectionCount > 0) {
        voiceDetectionCount = 0;
        console.log(`🔇 [잡음 필터링] 음량: ${level} (임계값 미달)`);
      }
    }
  });
}

/**
 * 🚫 3초 동안 음성 감지 안 됨 - 자동 중지
 */
function handleSilenceTimeout() {
  console.warn('🚫 [무음 감지] 3초 동안 음성이 감지되지 않았습니다.');

  if (!isTranslating.value) return;  // 이미 중지된 경우 무시

  // 1. 알림 표시
  silenceWarningShown.value = true;

  // 2. 녹음 취소 (서버 전송 안 함)
  cancelSilentRecording();

  // 3. UI 상태 업데이트 (완전 초기화)
  isTranslating.value = false;
  userInitiatedRecording.value = false;  // ✨ 사용자 액션 플래그 초기화
  audioLevel.value = 0;
  isVoiceDetected.value = false;
  voiceDetectionCount = 0;  // ✨ 카운터 초기화

  // 4. 타이머 정리 (이미 만료됐지만 명시적으로 null 처리)
  silenceTimer = null;

  // 5. 발언권 반납 (서버에 알림)
  fastApiWebSocket.send('finished_speaking');

  // 6. 3초 후 알림 자동 닫기
  setTimeout(() => {
    silenceWarningShown.value = false;
  }, 3000);

  console.log('🚫 [자동 중지] 완료 - 모든 상태 초기화됨');
}

/**
 * 🎤 음성 모니터링 중지
 */
function stopVoiceMonitoring() {
  console.log('🎤 [음성 모니터링] 중지');

  // 타이머 정리
  if (silenceTimer) {
    clearTimeout(silenceTimer);
    silenceTimer = null;
  }

  // 상태 초기화
  audioLevel.value = 0;
  isVoiceDetected.value = false;
  voiceDetectionCount = 0;
}

/**
 * 🎨 음성 레벨에 따른 버튼 테두리 스타일
 */
function getButtonBorderStyle() {
  if (!isTranslating.value || !isVoiceDetected.value) {
    return {};
  }

  // 음량에 따라 테두리 두께 변화 (2px ~ 6px)
  const borderWidth = 2 + (audioLevel.value / 100) * 4;

  // 음량에 따라 색상 강도 변화
  const greenIntensity = Math.min(255, 100 + (audioLevel.value / 100) * 155);
  const color = `rgb(74, ${greenIntensity}, 80)`;  // #4ade80 기반

  return {
    borderWidth: `${borderWidth}px`,
    borderColor: color,
    borderStyle: 'solid',
    boxShadow: `0 0 ${audioLevel.value / 10}px ${color}`
  };
}

// ============================================================================
// --- 신규: 발언권 제어 및 관련 핸들러 ---
// ============================================================================

/**
 * 서버로부터 'speaker_changed' 이벤트를 받았을 때 호출됩니다.
 * @param {object} data - { active_speaker: { user_id, user_name } | null }
 */
const handleSpeakerChanged = async (data) => {
  // 1. 서버가 보낸 새로운 발언자 정보를 받습니다.
  // 2. 현재 발언자 정보를 업데이트합니다.
  const newSpeaker = data.active_speaker;
  console.log('[디버깅] 발언자 변경 이벤트 수신:', data);
  console.log('[디버깅] 새 발언자 정보:', newSpeaker);
  console.log('[디버깅] 현재 사용자 ID:', userId.value);
  
  // 발언자 상태 업데이트
  activeSpeaker.value = newSpeaker;
  isRequestingToSpeak.value = false;

  // 내가 발언자인지 확인
  const amISpeaker = newSpeaker && newSpeaker.user_id === userId.value;
  console.log('[디버깅] 내가 발언자인가?', amISpeaker);
  
  if (amISpeaker && !isTranslating.value) {
    // ✨ 사용자가 버튼을 눌러 요청한 경우 userInitiatedRecording은 이미 true
    // 서버 자동 부여인 경우 userInitiatedRecording은 false 상태 유지
    console.log('[디버깅] 내가 발언자가 되어 번역 시작 시도 (userInitiated:', userInitiatedRecording.value, ')');
    try {
      const success = await startTranslation();
      if (success) {
        isTranslating.value = true;
        startVoiceMonitoring();  // ✨ 음성 모니터링 시작 (userInitiatedRecording 값에 따라 타이머 동작)
        console.log('[디버깅] 번역 시작 성공 + 음성 모니터링 활성화');
      } else {
        // 이 경우는 거의 발생하지 않아야 하지만, 안전장치로 남겨둡니다.
        console.log('[디버깅] 번역 시작 실패, 발언권 반납');
        userInitiatedRecording.value = false;  // ✨ 플래그 초기화
        fastApiWebSocket.send('finished_speaking');
      }
    } catch (error) {
      console.error('[디버깅] 마이크 시작 오류:', error);
      alert('마이크 시작에 실패했습니다. 권한을 확인해주세요.');
      userInitiatedRecording.value = false;  // ✨ 플래그 초기화
      fastApiWebSocket.send('finished_speaking');
    }
  } else if (!amISpeaker && isTranslating.value) {
    console.log('[디버깅] 내가 발언자가 아니게 되어 번역 중지');
    isTranslating.value = false;
    userInitiatedRecording.value = false;  // ✨ 플래그 초기화
    stopVoiceMonitoring();  // ✨ 음성 모니터링 중지
    // cancelTranslation()은 오디오 전송 없이 녹음만 중지합니다.
    // stopTranslation()은 전송까지 하므로 여기서는 사용하지 않습니다.
    console.log("[디버깅] 서버에 의해 발언권이 해제되어 녹음을 중지합니다.");
  }
  
  // isTranslationDisabled 계산된 속성의 현재 값 로그
  console.log('[디버깅] isTranslationDisabled 현재 값:', 
    activeSpeaker.value && activeSpeaker.value.user_id !== userId.value);
};

const handleSpeakRequestDenied = () => {
  isRequestingToSpeak.value = false;
  // 콘솔 로그만 남기고 주석 제거
  console.warn('발언권 요청이 거부되었습니다.');
};

// 번역 시작/종료 토글 함수 수정 - Jitsi 통합
async function toggleTranslation() {
  console.log('[디버깅] toggleTranslation 호출됨');
  console.log('[디버깅] 현재 상태:', {
    isTranslationDisabled: isTranslationDisabled.value,
    isRequestingToSpeak: isRequestingToSpeak.value,
    isTranslating: isTranslating.value,
    activeSpeaker: activeSpeaker.value
  });

  // 버튼이 비활성화 상태이면 아무것도 하지 않음
  if (isTranslationDisabled.value || isRequestingToSpeak.value) {
    console.log('[디버깅] 버튼이 비활성화 상태이거나 발언권 요청 중이므로 아무 작업도 수행하지 않음');
    return;
  }

  if (isTranslating.value) {
    console.log('[디버깅] 번역 종료 로직 실행');
    // 번역 종료 로직
    userInitiatedRecording.value = false;  // ✨ 사용자 액션 플래그 초기화
    isTranslating.value = false; // UI 즉시 반응
    stopVoiceMonitoring();  // ✨ 음성 모니터링 중지
    stopTranslation({
      sourceLanguage: sourceLanguage.value,
      code: meetingId,
      translationOption: translationOption.value,
      userId: userId.value,
      userName: userName.value,
    });
    // 발언권 반납
    console.log('[디버깅] 발언권 반납 메시지 전송');
    fastApiWebSocket.send('finished_speaking');
  } else {
    console.log('[디버깅] 번역 시작(발언권 요청) 로직 실행');
    // 번역 시작 (발언권 요청) 로직
    userInitiatedRecording.value = true;  // ✨ 사용자가 직접 버튼을 눌렀음을 표시
    isRequestingToSpeak.value = true;
    console.log('[디버깅] 발언권 요청 메시지 전송 (userInitiated: true)');
    fastApiWebSocket.send('request_to_speak');
  }
}

// 화면 공유 토글 함수 추가
function toggleScreenShare() {
  if (jitsiApi.value) {
    jitsiApi.value.executeCommand('toggleShareScreen');
    
    // 현재 화면 공유 상태 업데이트 (비동기)
    setTimeout(() => {
      // Jitsi API를 통해 현재 화면 공유 상태 확인
      try {
        const participant = jitsiApi.value.getParticipantsInfo()
          .find(p => p.participantId === jitsiApi.value.getParticipantsInfo()[0].participantId);
        
        isScreenSharing.value = participant && participant.isSharingScreen;
      } catch (error) {
        console.error('화면 공유 상태 확인 오류:', error);
      }
    }, 500);
  }
}

// Jitsi Meet API에 화면 공유 이벤트 리스너 추가
function addScreenShareListener() {
  if (jitsiApi.value) {
    jitsiApi.value.addListener('screenSharingStatusChanged', (event) => {
      isScreenSharing.value = event.on;
      console.log('화면 공유 상태 변경:', isScreenSharing.value ? '공유 중' : '공유 안 함');
    });
  }
}

// 회의 코드 복사 함수
function copyMeetingCode() {
  if (!joinCode.value) {
    navigator.clipboard.writeText(joinCode)
    .then(() => {
      codeCopied.value = true;
      setTimeout(() => {
        codeCopied.value = false;
      }, 3000); // 3초 후 다시 열쇠로
    });
    alert('회의 코드를 가져오는 중입니다. 잠시 후 다시 시도해주세요.');
    return;
  }
  
  navigator.clipboard.writeText(joinCode.value)
    .then(() => {
      codeCopied.value = true;
      setTimeout(() => {
        codeCopied.value = false;
      }, 3000);
    })
    .catch(err => {
      console.error('회의 코드 복사 오류:', err);
      alert('회의 코드 복사에 실패했습니다. 다시 시도하세요.');
    });
}

// 자원 정리
function cleanupResources() {
  // Jitsi Meet API 정리
  if (jitsiApi.value) {
    jitsiApi.value.dispose();
    jitsiApi.value = null;
  }

  // FastAPI WebSocket 연결 종료
  fastApiWebSocket.disconnect();

  // 번역 관련 리소스 정리
  cleanupTranslation();
}

// Jitsi Meet 초기화 (WebRTC 및 시그널링 코드 대체)
function initJitsiMeet() {
  // Jitsi Meet API 로드 (apiConfig.js 값 사용)
  const script = document.createElement('script');
  script.src = jitsiScriptUrl;
  script.async = true;
  script.onload = () => {
    // Jitsi API 인스턴스 생성
    const domain = jitsiServerURL;
    const options = {
      roomName: `${jaasAppId}/${meetingId}`,
      width: '100%',
      height: '100%',
      parentNode: document.getElementById('jitsi-container'),
      userInfo: {
        displayName: userName.value,
        email: ''
      },
      configOverwrite: {
        prejoinPageEnabled: false,
        startWithAudioMuted: !isAudioEnabled.value,
        startWithVideoMuted: !isVideoEnabled.value,
        disableDeepLinking: true,
        // 로그 레벨 설정 (추가)
        logging: {
          defaultLogLevel: 'error' // 'error'만 표시 ('debug', 'info', 'log', 'warn', 'error')
        }
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        DEFAULT_BACKGROUND: '#3c4043',
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
        TOOLBAR_BUTTONS: [],
        // 로고와 상단 헤더 숨기기 추가
        DEFAULT_LOGO_URL: '',
        SHOW_BRAND_WATERMARK: false,
        SHOW_POWERED_BY: false,
        DISPLAY_WELCOME_PAGE_CONTENT: false,
        DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT: false,
        TOOLBAR_ALWAYS_VISIBLE: true,
        SHOW_CHROME_EXTENSION_BANNER: false
      }
    };

    try {
      jitsiApi.value = new window.JitsiMeetExternalAPI(domain, options);
      console.log('Jitsi Meet API 초기화 성공');

      // 화면 공유 이벤트 리스너 추가
      addScreenShareListener();
      
    } catch (error) {
      console.error('Jitsi Meet 초기화 오류:', error);
      alert('화상 회의 초기화에 실패했습니다.');
    }
  };
  
  document.body.appendChild(script);
}

// 자막 이벤트 핸들러 등록
function initSubtitleHandler() {
  fastApiWebSocket.addEventListener('subtitle', (data) => {
    console.log('자막 이벤트 수신:', data);
    
     // 데이터 구조 확인 (문자열 또는 객체일 수 있음)
     let text, speakerName;
    
    if (typeof data === 'string') {
      text = data;
      speakerName = userName.value; // 기본값으로 본인 이름
    } else {
      text = data.text || data.translated_text || data;
      speakerName = data.speaker_name || data.userName || userName.value;
    }

    // 이전 타이머가 있으면 제거
    if (subtitleTimeout.value) {
      clearTimeout(subtitleTimeout.value);
    }
    
    // 자막 표시
    subtitle.value = text;

    // 자막 길이에 따른 표시 시간 계산 최소 5초, 최대는 15초 글자당 0.1초 기준
    const displayTime = Math.max(5000, Math.min(15000, text.length * 100));
    
    // 설정된 시간 후에 자막을 숨기고 스크립트에 저장
    subtitleTimeout.value = setTimeout(() => {
       // 여기서 실제 발화자 이름 사용!
      saveToScript(speakerName, text);  // 실제 발화자 이름으로 저장
      subtitle.value = '';
    }, displayTime);
  });
}


// 웹소켓 연결 함수 분리
function initWebSocketConnection() {
  console.log('[디버깅] 웹소켓 연결 초기화 시작 - 파라미터:', {
    meetingId,
    userId: userId.value,
    userName: userName.value,
    sourceLanguage: sourceLanguage.value
  });
  
  // 기존 연결 확인 및 닫기
  if (window.fastApiWs && window.fastApiWs.readyState === WebSocket.OPEN) {
    console.log('[디버깅] 이미 열린 웹소켓 연결이 있습니다. 재사용합니다.');
    return;
  }
  
  // FastAPI WebSocket 연결 시작
  fastApiWebSocket.connect(meetingId, userId.value, userName.value, sourceLanguage.value);
  // --- 신규: 발언권 관련 이벤트 리스너 등록 ---
  console.log('[디버깅] 발언권 관련 이벤트 리스너 등록');
  fastApiWebSocket.addEventListener('speaker_changed', handleSpeakerChanged);
  fastApiWebSocket.addEventListener('speak_request_denied', handleSpeakRequestDenied);
  initRagResultHandler();
  initSubtitleHandler();
  
  // 연결 상태 확인
  setTimeout(() => {
    if (window.fastApiWs) {
      console.log('[디버깅] WebSocket 연결 상태:', 
        window.fastApiWs.readyState === WebSocket.OPEN ? '연결됨' : '연결 안됨');
    } else {
      console.warn('[디버깅] window.fastApiWs가 존재하지 않습니다.');
    }
  }, 2000);
}

// 간단한 함수들
function toggleScriptPanel() {  // 패널 열고 닫기
  isScriptPanelOpen.value = !isScriptPanelOpen.value

    // 패널이 열리면 알림 상태 제거
    if (isScriptPanelOpen.value) {
    hasNewScriptMessage.value = false
  }
}

//스크립트 저장 핵심로직
function saveToScript(speaker, text) {   // 스크립트에 메시지 저장
  if (!text || text.trim() === '') return
  
  console.log(`📝 스크립트 저장: [${speaker}] ${text}`); // 디버깅 로그

  const message = {
    speaker: speaker || userName.value || '알 수 없음', // userName.value 우선 사용
    text: text.trim(),
    timestamp: new Date()
  }
  
  // 메시지를 한 번만 추가
  scriptHistory.value.push(message)
  
  // 최대 50개까지만 유지
  if (scriptHistory.value.length > 50) {
    scriptHistory.value.shift()
  }

  // 패널이 닫혀있을 때만 깜빡이는 효과 실행
  if (!isScriptPanelOpen.value) {
    triggerNewMessageAnimation()
  }
  
  console.log('스크립트에 저장됨:', message)
}

// 새 메시지 애니메이션 트리거 함수 추가
function triggerNewMessageAnimation() {  // 깜빡이는 효과 트리거
  hasNewScriptMessage.value = true
  
  // 2초 후 애니메이션 클래스 제거 (애니메이션 지속시간 * 2번)
  setTimeout(() => {
    hasNewScriptMessage.value = false
  }, 5000)
}

function closeScriptPanel() {
  isScriptPanelOpen.value = false
}

// 줄바꿈 처리 함수 추가
function formatContent(content) {
  if (!content) return '';
  // \n을 <br>로 변환하고 HTML 이스케이프 처리
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br>');
}

onMounted(() => {
  console.log('[디버깅] MeetingRoom 컴포넌트 마운트 시작');
  
  // 1. 회의 무효화 확인
  if (checkMeetingRevocation()) {
    return; // 무효화된 회의면 중단
  }
  
  // 2. 토큰 교차 검증 (URL + 세션스토리지)
  if (!validateTokenCrossCheck()) {
    console.error('[디버깅] 토큰 교차 검증 실패');
    alert('올바른 경로로 회의에 접근해주세요. URL 복사를 통한 접근은 허용되지 않습니다.');
    router.push('/dashboard');
    return;
  }
  
  // 3. 사용자 정보 설정
  if (!userId.value) {
    setupUserInfoFromSession();
  }
  console.log('[디버깅] 사용자 정보:', { userId: userId.value, userName: userName.value });
  
  // 4. 페이지 이탈 이벤트 핸들러 등록
  setupPageUnloadHandlers();
  
  console.log('[디버깅] 토큰 교차 검증 성공, 회의 초기화 진행');
  
  // 5. 기존 초기화 로직 실행
  initJitsiMeet();
  initTranslationOptions();
  initWebSocketConnection();//웹소켓 연결 초기화
  
  document.addEventListener('click', closeDropdownOnOutsideClick);
  
  setTimeout(() => {
    if (!window.fastApiWs || window.fastApiWs.readyState !== WebSocket.OPEN) {
      console.log('[디버깅] 웹소켓 연결이 감지되지 않아 수동으로 연결 시도');
      initWebSocketConnection();
    } else {
      console.log('[디버깅] 웹소켓 이미 연결됨');
    }
  }, 1000);

  const urlParams = new URLSearchParams(window.location.search);
  const encodedUserName = urlParams.get('userName');
  if (encodedUserName) {
    try {
      userName.value = decodeURIComponent(atob(encodedUserName));
    } catch (e) {
      console.warn('userName 디코딩 실패:', e);
      userName.value = '';
    }
  }
});

onBeforeUnmount(() => {
  console.log('MeetingRoom 컴포넌트 언마운트');
  
  // 자원 정리
  cleanupResources();
  
  // ⚠️ 컴포넌트 언마운트 시에는 sessionStorage만 정리
  cleanupTokens();
  
  // 이벤트 리스너 제거
  fastApiWebSocket.removeEventListener('speaker_changed', handleSpeakerChanged);
  fastApiWebSocket.removeEventListener('speak_request_denied', handleSpeakRequestDenied);
  document.removeEventListener('click', closeDropdownOnOutsideClick);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<style src="@/assets/styles/MeetingRoom.css"></style>
<style scoped>
.speaker-status-banner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.65);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  z-index: 1001;
  text-align: center;
  pointer-events: none; /* 배너 뒤로 클릭이 가능하도록 설정 */
}

.btn.is-disabled {
  background-color: #6c757d; /* 비활성화 시 회색 배경 */
  cursor: not-allowed;
  opacity: 0.65;
}

/* ============================================================================ */
/* 🎤 음성 감지 관련 스타일 */
/* ============================================================================ */

/* 🚫 무음 경고 배너 */
.silence-warning-banner {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
  z-index: 1002;
  max-width: 90%;
}

.warning-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.warning-content i.fa-microphone-slash {
  font-size: 20px;
}

.warning-content span {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
}

.warning-content .close-btn {
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 16px;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.warning-content .close-btn:hover {
  opacity: 1;
}

/* 배너 나타날 때 애니메이션 */
.silence-banner-enter-active {
  animation: slideDown 0.4s ease-out;
}

/* 배너 사라질 때 애니메이션 */
.silence-banner-leave-active {
  animation: fadeOut 0.8s ease-out;
}

/* 배너 슬라이드 다운 애니메이션 */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translate(-50%, -30px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* 배너 페이드 아웃 애니메이션 */
@keyframes fadeOut {
  0% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -20px);
  }
}

/* 🎤 음성 감지 시 버튼 펄스 애니메이션 */
.btn.voice-active {
  animation: voicePulse 0.8s ease-in-out infinite;
}

@keyframes voicePulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

/* 🎨 버튼 테두리 스타일 부드러운 전환 */
.translation-control .btn {
  transition: border-width 0.1s ease-out,
              border-color 0.1s ease-out,
              box-shadow 0.1s ease-out;
}
</style>